import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useTimeline } from '../TimelineContext';
import { useChart } from '../../../../components/chart';
import { getSensorType } from '../../../../services/localizationService';
import { buildCollapsedTimeline, realToCollapsed, collapsedToReal } from '../gapCollapse';

export default function TimelineSensorChartWidget({
  recordings = [],
  events = [],
  recordingMetadata = {},
  widgetConfig,
  onUpdateWidget,
}) {
  const { currentTime, seek, window: timeWindow } = useTimeline();
  const selectedSensorType = widgetConfig?.selectedSensorType || '';

  // Build collapsed timeline from all recordings
  const collapsedTimeline = useMemo(() => {
    const recs = recordings.map((r) => ({ id: r.id, startTime: r.startTime, endTime: r.endTime }));
    return buildCollapsedTimeline(recs);
  }, [recordings]);

  const hasCollapsedTimeline = collapsedTimeline.segments.length > 0;

  // Collect all unique sensor types across all recordings
  const allSensorTypes = useMemo(() => {
    const types = new Set();
    recordings.forEach((rec) => {
      Object.keys(rec.data || {}).forEach((key) => {
        const sensorType = key.split(':').pop();
        if (!['altitude', 'latitude', 'longitude'].includes(sensorType.toLowerCase())) {
          types.add(sensorType);
        }
      });
    });
    return [...types].sort();
  }, [recordings]);

  // Auto-select first sensor type if none selected
  const activeSensorType = selectedSensorType || allSensorTypes[0] || '';

  // Build series: one per recording that has this sensor type
  // Also build a realTimes lookup for tooltip display
  const { series, realTimesMap } = useMemo(() => {
    if (!activeSensorType) return { series: [], realTimesMap: {} };

    const resultSeries = [];
    const timesMap = {};

    recordings.forEach((rec) => {
      const matchingKey = Object.keys(rec.data || {}).find(
        (k) => k.split(':').pop().toLowerCase() === activeSensorType.toLowerCase()
      );
      if (!matchingKey || !rec.data[matchingKey]) return;

      const meta = recordingMetadata[rec.id];
      const color = meta?.color || '#1976d2';
      const recName = meta?.label || rec.name || 'Unknown';
      const points = rec.data[matchingKey];

      const seriesRealTimes = [];
      const data = hasCollapsedTimeline
        ? points
            .map((dp) => {
              const collapsed = realToCollapsed(dp.time, collapsedTimeline);
              seriesRealTimes.push(dp.time);
              return [collapsed ?? dp.time, dp.value];
            })
            .filter((dp) => dp[0] !== null)
        : points.map((dp) => {
            seriesRealTimes.push(dp.time);
            return [dp.time, dp.value];
          });

      const seriesIdx = resultSeries.length;
      timesMap[seriesIdx] = seriesRealTimes;

      resultSeries.push({
        name: recName,
        data,
        color,
      });
    });

    return { series: resultSeries, realTimesMap: timesMap };
  }, [recordings, activeSensorType, recordingMetadata, hasCollapsedTimeline, collapsedTimeline]);

  // Build gap indicator annotations
  const gapAnnotations = useMemo(() => {
    if (!hasCollapsedTimeline) return [];
    return collapsedTimeline.segments
      .filter((seg) => seg.type === 'gap')
      .map((seg) => ({
        x: seg.collapsedStart,
        x2: seg.collapsedEnd,
        fillColor: 'rgba(0,0,0,0.06)',
        opacity: 1,
        label: { text: '' },
      }));
  }, [hasCollapsedTimeline, collapsedTimeline]);

  // Collect all rule events across all recordings
  const eventAnnotations = useMemo(() => {
    if (!events || events.length === 0) return [];
    if (hasCollapsedTimeline) {
      return events
        .map((evt) => {
          const x = realToCollapsed(new Date(evt.startTime).getTime(), collapsedTimeline);
          const x2 = realToCollapsed(new Date(evt.endTime).getTime(), collapsedTimeline);
          return x != null && x2 != null ? { x, x2, fillColor: '#d50100', label: { text: evt.label } } : null;
        })
        .filter(Boolean);
    }
    return events.map((evt) => ({
      x: new Date(evt.startTime).getTime(),
      x2: new Date(evt.endTime).getTime(),
      fillColor: '#d50100',
      label: { text: evt.label },
    }));
  }, [events, hasCollapsedTimeline, collapsedTimeline]);

  // Map currentTime playhead to collapsed space
  const collapsedCurrentTime = hasCollapsedTimeline ? realToCollapsed(currentTime, collapsedTimeline) : currentTime;

  // Compute collapsed min/max for x-axis range
  const collapsedMin = hasCollapsedTimeline
    ? timeWindow?.start != null
      ? realToCollapsed(timeWindow.start, collapsedTimeline)
      : 0
    : timeWindow?.start;
  const collapsedMax = hasCollapsedTimeline
    ? timeWindow?.end != null
      ? realToCollapsed(timeWindow.end, collapsedTimeline)
      : collapsedTimeline.totalCollapsedDuration
    : timeWindow?.end;

  const chartOptions = useChart({
    plotOptions: { bar: { columnWidth: '16%' } },
    fill: { type: ['solid'] },
    colors: series.map((s) => s.color),
    xaxis: hasCollapsedTimeline
      ? {
          type: 'numeric',
          min: collapsedMin ?? 0,
          max: collapsedMax ?? collapsedTimeline.totalCollapsedDuration,
          labels: {
            formatter: (val) => {
              const realTime = collapsedToReal(val, collapsedTimeline);
              if (realTime == null) return '';
              const d = new Date(realTime);
              return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            },
          },
          tickAmount: 8,
        }
      : {
          type: 'datetime',
          min: timeWindow?.start,
          max: timeWindow?.end,
          labels: { datetimeUTC: false },
        },
    annotations: {
      xaxis: [
        ...gapAnnotations,
        ...eventAnnotations,
        ...(collapsedCurrentTime != null
          ? [
              {
                x: collapsedCurrentTime,
                borderColor: '#775DD0',
                label: {
                  borderColor: '#775DD0',
                  style: { color: '#fff', background: '#775DD0' },
                  text: 'Current Time',
                },
              },
            ]
          : []),
      ],
    },
    chart: {
      toolbar: {
        show: true,
        tools: { download: true, selection: true, pan: true, zoom: true },
      },
      events: {
        markerClick: (_, chartContext, opts) => {
          const { seriesIndex } = opts;
          const { dataPointIndex } = opts;
          // Seek to real time, not collapsed time
          const realTime = realTimesMap[seriesIndex]?.[dataPointIndex];
          if (realTime != null) {
            seek(realTime);
          }
        },
      },
    },
    stroke: { width: 2, curve: 'smooth' },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
    },
    tooltip: {
      shared: false,
      custom: ({ series: seriesData, seriesIndex, dataPointIndex, w }) => {
        const value = seriesData[seriesIndex]?.[dataPointIndex] ?? 'N/A';
        const color = w.globals.colors[seriesIndex];
        const name = w.globals.seriesNames[seriesIndex];
        // Show real time in tooltip
        let timeStr = '';
        if (hasCollapsedTimeline) {
          const realTime = realTimesMap[seriesIndex]?.[dataPointIndex];
          if (realTime != null) {
            const d = new Date(realTime);
            timeStr = `<div style="font-size:11px;color:#666;margin-bottom:4px">${d.toLocaleString()}</div>`;
          }
        }
        return `
          <div style="padding: 8px; border-radius: 9px;">
            ${timeStr}
            <div style="display: flex; align-items: center;">
              <span style="background-color: ${color}; border-radius: 50%; width: 12px; height: 12px; margin-right: 8px;"></span>
              <span>${name}: <strong>${value}</strong></span>
            </div>
          </div>
        `;
      },
    },
  });

  if (recordings.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary" variant="body2">
          No recordings in workspace
        </Typography>
      </Box>
    );
  }

  if (allSensorTypes.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography color="text.secondary" variant="body2">
          No sensor data available
        </Typography>
      </Box>
    );
  }

  // Convert series to ApexCharts format (array of [x, y])
  const apexSeries = series.map((s) => ({
    name: s.name,
    type: 'line',
    data: s.data,
  }));

  return (
    <Box sx={{ px: 1.5, py: 2.25, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }} dir="ltr">
      <FormControl size="small" sx={{ mb: 0.5, maxWidth: 220, flexShrink: 0 }}>
        <InputLabel>Sensor Type</InputLabel>
        <Select
          value={activeSensorType}
          label="Sensor Type"
          onChange={(e) => onUpdateWidget?.({ selectedSensorType: e.target.value })}
        >
          {allSensorTypes.map((t) => (
            <MenuItem key={t} value={t}>
              {getSensorType(t)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {apexSeries.length > 0 ? (
          <ReactApexChart type="line" series={apexSeries} options={chartOptions} height="100%" />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography color="text.secondary" variant="body2">
              No recordings contain {getSensorType(activeSensorType)} data
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
